import axios from 'axios';
import { API_BASE_URL, ALERTEXPOST_BASE_URL, REPORTS_BASE_URL, CHECK_RESET_PASSWORD_BASE_URL, CADASTRAL_DATA_BASE_URL } from '@/constants.js';
import { getJwtIDToken } from '@/api/api.js';

// ------------------ CLIENT CON COGNITO JWT ---------------------

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

const apiCadastralClient = axios.create({
  baseURL: CADASTRAL_DATA_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

const alertExPostApiClient = axios.create({
  baseURL: ALERTEXPOST_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

const reportsApiClient = axios.create({
  baseURL: REPORTS_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

apiClient.interceptors.request.use(async config => {
  try {
    const token = await getJwtIDToken();
    config.headers.Authorization = token ? `Bearer ${token}` : '';
  } catch (error) {
    config.headers.Authorization = '';
  }

  return config;
}, error => Promise.reject(error));

apiCadastralClient.interceptors.request.use(async config => {
  try {
    const token = await getJwtIDToken();
    config.headers.Authorization = token ? `Bearer ${token}` : '';
  } catch (error) {
    config.headers.Authorization = '';
  }

  return config;
}, error => Promise.reject(error));

alertExPostApiClient.interceptors.request.use(async config => {
  try {
    const token = await getJwtIDToken();
    config.headers.Authorization = token ? `Bearer ${token}` : '';
  } catch (error) {
    config.headers.Authorization = '';
  }

  return config;
}, error => Promise.reject(error));

reportsApiClient.interceptors.request.use(async config => {
  try {
    const token = await getJwtIDToken();
    config.headers.Authorization = token ? `Bearer ${token}` : '';
  } catch (error) {
    config.headers.Authorization = '';
  }

  return config;
}, error => Promise.reject(error));

// ------------------ CLIENT CON API KEY ---------------------

const apiKeyClient = axios.create({
  baseURL: CHECK_RESET_PASSWORD_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': process.env.VUE_APP_CHECK_RESET_PASSWORD_API_KEY
  }
});

// ------------------ API CON API KEY ---------------------

export const checkUserPasswordReset = async username => {
  return apiKeyClient.get('/', {
    params: { username }
  });
};

export const setUserPasswordReset = async (username, passwordReset) => {
  return apiKeyClient.post('/', {
    username,
    passwordReset
  });
};

// ------------------ API CON COGNITO ---------------------

export const getSeaDistance = async (latitude, longitude) => {
  return apiClient.get(`/fe_sea_distance?LATITUDE=${latitude}&LONGITUDE=${longitude}`);
};

export const getLegendValues = async (layerName, layerStyle) => {
  return apiClient.get(`/fe_get_legend/geoserver/wms?service=WMS&version=1.1.0&request=GetLegendGraphic&layer=${layerName}&style=${layerStyle}&format=application/json`);
};

export const getWmtsDescribeDomains = async (layer, time, service_id, policy_id) => {
  let url = `/fe_describe_domains/geoserver/gwc/service/wmts?request=DescribeDomains&Version=1.0.0&Layer=${layer}&TileMatrixSet=EPSG:4326&expandLimit=0&time=${time}`;

  if (service_id != undefined && policy_id != undefined) {
    url += `&bbox=${service_id}/${policy_id}`;
  }

  return apiClient.get(url);
};

export const getWmtsGetDomainValues = async (domain, layer, time, limit) => {
  return apiClient.get(`/fe_describe_domains/geoserver/gwc/service/wmts?request=GetDomainValues&Version=1.0.0&Layer=${layer}&Domain=${domain}${limit ? `&Limit=${limit}` : ''}&FromValue=${time}`);
};

export const getCapabilities = async (ws, layer, type) => {
  const service = type === 'VECTOR' ? 'wfs' : 'wms';

  return apiClient.get(`/fe_describe_domains/geoserver/${ws}/${layer}/ows?service=${service}&version=1.3.0&request=GetCapabilities`);
};

export const getVectorLayer = async (layer, extent, time, username, service_name, feature_id, group, param_range, remove_geometry, aggregation_interval, udm_conversions) => {
  let url = `/fe_ogc/geoserver/wfs?SERVICE=WFS&VERSION=1.0.0&REQUEST=GetFeature&TYPENAME=${layer}&OUTPUTFORMAT=application/json&SRSNAME=EPSG:3857&BBOX=${extent.join(',')},EPSG:3857&TIME=${time}&USERNAME=${username}&SERVICE_NAME=${service_name}&MAXFEATURES=2000&GROUP=${group}`;

  if (feature_id != undefined) {
    url += `&FEATURE_ID=${feature_id}`;
  }

  if (param_range != undefined) {
    url += `&PARAM_RANGE=${param_range}`;
  }

  if (remove_geometry != undefined) {
    url += `&REMOVE_GEOMETRY=${remove_geometry}`;
  }

  if (aggregation_interval) {
    url += `&AGGREGATION_INTERVAL=${aggregation_interval}`;
  }

  if (udm_conversions) {
    url += `&UDM_CONVERSIONS=${encodeURIComponent(udm_conversions)}`;
  }

  return apiClient.get(url);
};

export const getVectorChart = async (layer, extent, time, username, service_name, feature_id, group, remove_geometry, aggregation_interval, udm_conversions) => {
  const featureID = typeof feature_id == 'string' ? `'${feature_id}'` : feature_id;
  let url = `/fe_ogc/geoserver/wfs?SERVICE=WFS&VERSION=1.0.0&REQUEST=GetFeature&TYPENAME=${layer}&OUTPUTFORMAT=application/json&SRSNAME=EPSG:3857&BBOX=${extent.join(',')},EPSG:3857&TIME=${time}&USERNAME=${username}&SERVICE_NAME=${service_name}&FEATURE_ID=${featureID}&MAXFEATURES=2000&GROUP=${group}`;

  if (remove_geometry != undefined) {
    url += `&REMOVE_GEOMETRY=${remove_geometry}`;
  }

  if (aggregation_interval) {
    url += `&AGGREGATION_INTERVAL=${aggregation_interval}`;
  }

  if (udm_conversions) {
    url += `&UDM_CONVERSIONS=${encodeURIComponent(udm_conversions)}`;
  }

  return apiClient.get(url);
};

export const getLayerCoordinatesValues = async (data) => {
  let urlParameters = `USERNAME=${data.username}&WORKSPACE=${data.workspace}&LAYERS=${data.layers}&LATITUDE=${data.latitude}&LONGITUDE=${data.longitude}&TIME=${data.time}&OUTPUT=${data.output}&TIMEZONE=${data.timezone}&SERVICE_NAME=${data.service_name}&GROUP=${data.group}`;

  const buildUrl = () => {
    if (data.runtime) {
      urlParameters += `&RUNTIME=${data.runtime}`;
    }

    if (data.udm_conversions) {
      urlParameters += `&UDM_CONVERSIONS=${encodeURIComponent(data.udm_conversions)}`;
    }

    return `/fe_custom?${urlParameters}`;
  };

  return apiClient.get(buildUrl());
};

export const getAlertExPost = async url => {
  return alertExPostApiClient.get(`/${url}`);
};

export const postAlertExPost = async (url, body) => {
  return alertExPostApiClient.post(`/${url}`, body);
};

export const getReports = async (published, organizationID, type, year, page) => {
  return reportsApiClient.get(`/reports?published=${published}&organizationId=${organizationID}&reportType=${type}&pageSize=100&page=${page}&sort_by=desc(reportDate)&from=${year}-01-01&to=${year}-12-31`);
};

export const getSingleReport = async (reportID) => {
  return reportsApiClient.get(`/reports/${reportID}`);
};

export const getLatestReport = async (organizationID, type) => {
  return reportsApiClient.get(`/reports?pageSize=1&published=true&organizationId=${organizationID}&reportType=${type}&sort_by=desc(reportDate)`);
};

export const getOldestReport = async (organizationID) => {
  return reportsApiClient.get(`/reports?pageSize=1&published=true&organizationId=${organizationID}&sort_by=asc(reportDate)`);
};

export const getReportTemplate = async (templateID) => {
  return reportsApiClient.get(`/reports/templates/${templateID}`);
};

export const getReportGEOJSON = async (reportID, areaID) => {
  return reportsApiClient.get(`/kml/${reportID}/${areaID}`);
};

export const getReportPdf = async (organizationID, reportID, digest) => {
  return reportsApiClient.get(`/report/${organizationID}/${reportID}/${digest}/download`, { responseType: 'blob' });
};

export const getLegendReport = async (organizationID, digestLegend) => {
  return reportsApiClient.get(`/report/legend/${organizationID}/${digestLegend}`, { responseType: 'blob' });
};

export const getReportOrganization = async () => {
  return reportsApiClient.get('/organizations?pageSize=500');
};

export const getRegionsAndProvinces = async () => {
  return apiCadastralClient.get('/regions-with-provinces');
};

export const getMunicipality = async (regionCode, provinceCode) => {
  return apiCadastralClient.get(`/regions/${regionCode}/provinces/${provinceCode}/communes`);
};

export const getSheets = async (municipalityCode) => {
  return apiCadastralClient.get(`/municipalities/${municipalityCode}/sheets`);
};

export const getParcels = async (municipalityCode, sheetId, regionCode) => {
  return apiCadastralClient.get(`/municipalities/${municipalityCode}/sheets/${sheetId}/parcels`, {
    params: regionCode ? { regionCode: regionCode.trim().toUpperCase() } : {}
  });
};

export const getMunicipalityParcels = async (municipalityCode, provinceCode) => {
  return apiCadastralClient.get(`/municipalities/${municipalityCode}/parcels`, {
    params: { provinceCode }
  });
};
